// src/pages/ManutenzioniCompilate.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { generateManutenzionePdfBlob } from '../pdf/clientPdf';
import { Card, Checkbox } from '../components/ui';

const API_BASE = '/api';

const templates = [
  {
    id: 'CIESSE_IMPIANTI',
    label: 'Ciesse Impianti',
    file: '/Manutenzione/CiesseImpianti.pdf',
  },
  {
    id: 'CIESSE_SERVICE',
    label: 'Ciesse Service',
    file: '/Manutenzione/CiesseService.pdf',
  },
];

const CHECK_LABELS = {
  1: 'Verifica 1: Controllo e pulizia evaporatore: con aspiratore',
  2: 'Verifica 2: Controllo ventole evaporatore: verifica stato e pulizia',
  3: 'Verifica 3: Controllo e pulizia bacinella evaporatore: con aspiratore',
  4: 'Verifica 4: Controllo sistema di scarico condensa: pulizia e verifica funzionamento',
  5: 'Verifica 5: Controllo serraggi morsetti elettrici: verifica e ripristino',
  6: 'Verifica 6: Controllo Relè e Relè a stato solido: verifica visiva e stato dei contatti',
  7: 'Verifica 7: Controllo e pulizia condensatore: con aspiratore',
  8: 'Verifica 8: Verifica assorbimento elettrico',
  9: 'Verifica 9: Controllo e ripristino pressione gas refrigerante (con sonda e manometri)',
  10: 'Verifica 10: Controllo dispositivi generatori di vapore: controllo caldaia, termostato, dispositivi di controllo',
  11: 'Verifica 11: Controllo dispositivi di sicurezza: simulazione intervento allarmi',
  12: 'Verifica 12: Controllo sistema di regolazione della Temperatura: Misurazione della temperatura interna in centro camera:',
  13: 'Verifica 13: Controllo sistema di regolazione Umidità relativa: Misurazione della UR% interna in centro camera:',
  14: 'Verifica 14: Controllo dispositivi di sicurezza: simulazione intervento allarmi',
  15: 'Verifica 15: Note aggiuntive',
};


const ManutenzioniCompilate = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const fromWip = location.state?.fromWorkInProgress || {};
  const workInProgressId = fromWip._id || null;
  const order = fromWip.workOrder || {};
  const summary = fromWip.summary || {};
  const savedFormData = fromWip.formData || {};
  const savedChecks = fromWip.checks || {};
  const savedTecnicoSignature = fromWip.tecnicoSignature || null;
  const savedClienteSignature = fromWip.clienteSignature || null;

  const workOrderIdFromState = location.state?.workOrderId || null;

  // Se non ho WIP nello state, provo a recuperarlo dal backend via workOrderId
  useEffect(() => {
    const loadWip = async () => {
      try {
        if (workInProgressId) return; // già ok
        if (!workOrderIdFromState) return;

        const res = await authFetch(
          `${API_BASE}/work/in-progress/by-workorder/${workOrderIdFromState}`
        );
        if (!res.ok) return;

        const wip = await res.json();

        // 🔥 “rimpiazzo” i dati locali
        // NB: qui non puoi riassegnare fromWip (è const), quindi aggiorni gli state
        const data = wip?.workInProgress || wip;

        if (data?.formData) {
          setFormData((prev) => ({
            ...prev,
            ...data.formData,
            data: data.formData.data || prev.data,
          }));
        }
        if (data?.checks) {
          setChecks((prev) => ({
            ...prev,
            ...data.checks,
          }));
        }
        if (data?.tecnicoSignature) setTecnicoSignature(data.tecnicoSignature);
        if (data?.clienteSignature) setClienteSignature(data.clienteSignature);
      } catch (e) {
        console.error('Errore load WIP (ManutenzioniCompilate):', e);
      }
    };

    loadWip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderIdFromState]);

  const [selectedTemplate, setSelectedTemplate] = useState('CIESSE_IMPIANTI');

  // =========================
  // FORM DATA PRECARICATO
  // =========================
  const [formData, setFormData] = useState(() => {
    // calcolo data di fallback
    let fallbackData = '';
    if (summary.data) {
      fallbackData = summary.data;
    } else if (order.dataIntervento || order.dateIntervento || order.data) {
      const raw = order.dataIntervento || order.dateIntervento || order.data;
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) {
        fallbackData = d.toISOString().slice(0, 10);
      }
    }

    const fd = { ...savedFormData };

    if (!fd.data) {
      fd.data = fallbackData || '';
    }
    if (!fd.cliente) {
      fd.cliente =
        summary.cliente ||
        order.cliente ||
        order.companyName ||
        '';
    }
    if (!fd.impianto) {
      fd.impianto =
        summary.impianto ||
        order.impianto ||
        order.presidio ||
        order.equipmentPresidio ||
        '';
    }
    if (!fd.luogo) {
      fd.luogo =
        summary.luogo ||
        order.luogo ||
        order.luogoIntervento ||
        order.indirizzo ||
        order.place ||
        '';
    }
    if (!fd.note) {
      fd.note =
        summary.note ||
        summary.noteGenerali ||
        order.descrizione ||
        '';
    }

    return {
      data: fd.data || '',
      cliente: fd.cliente || '',
      impianto: fd.impianto || '',
      luogo: fd.luogo || '',
      note: fd.note || '',
      // se in futuro aggiungi altri campi alla manutenzione, restano comunque dentro fd
      ...fd,
    };
  });

  // =========================
  // CHECKS PRECARICATI
  // =========================
  const defaultChecks = {
    1: { ok: false },
    2: { ok: false },
    3: { ok: false },
    4: { ok: false },
    5: { ok: false },
    6: { ok: false },
    7: { ok: false },
    8: { ok: false, note: '' },
    9: { ok: false, na: false, value: '' },
    10: { ok: false, na: false },
    11: { ok: false },
    12: { ok: false, na: false, tSet: '', tMis: '' },
    13: { ok: false, na: false, urSet: '', urMis: '' },
    14: { ok: false, na: false },
    15: { ok: false, note: '' },
  };

  const [checks, setChecks] = useState(() => {
    if (savedChecks && Object.keys(savedChecks).length > 0) {
      // merge per sicurezza: se in futuro aggiungi nuove verifiche
      return {
        ...defaultChecks,
        ...savedChecks,
      };
    }
    return defaultChecks;
  });

  // =========================
  // FIRME PRECARICATE
  // =========================
  const [tecnicoSignature, setTecnicoSignature] = useState(savedTecnicoSignature);
  const [clienteSignature, setClienteSignature] = useState(savedClienteSignature);

  const [signModal, setSignModal] = useState({ open: false, type: null });
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [pdfUrl, setPdfUrl] = useState(
    templates.find((t) => t.id === selectedTemplate)?.file || null
  );

  // aggiorna anteprima PDF
  useEffect(() => {
    const t = templates.find((tmp) => tmp.id === selectedTemplate);
    setPdfUrl(t ? t.file : null);
  }, [selectedTemplate]);

  // blocca scroll quando la modale firme è aperta
  useEffect(() => {
    if (signModal.open) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
    return undefined;
  }, [signModal.open]);

  // =========================
  // HANDLER FORM / CHECK / FIRMA
  // =========================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleCheck = (num, field) => {
    setChecks((prev) => {
      const current = prev[num] || {};
      const nextValue = !current[field];

      const next = {
        ...prev,
        [num]: {
          ...current,
          [field]: nextValue,
        },
      };

      // ✅ Mutua esclusione OK ↔ N/A
      if (field === 'ok' && nextValue === true && 'na' in current) {
        next[num].na = false;
      }

      if (field === 'na' && nextValue === true) {
        next[num].ok = false;

        // pulisco campi valori quando NA
        if ('value' in current) next[num].value = '';
        if ('tSet' in current) next[num].tSet = '';
        if ('tMis' in current) next[num].tMis = '';
        if ('urSet' in current) next[num].urSet = '';
        if ('urMis' in current) next[num].urMis = '';
        if ('note' in current) next[num].note = '';
      }

      // se tolgo OK alla 8, pulisco nota
      if (num === 8 && field === 'ok' && nextValue === false) {
        next[num].note = '';
      }

      return next;
    });
  };


  const handleCheckValueChange = (num, field, value) => {
    setChecks((prev) => ({
      ...prev,
      [num]: {
        ...prev[num],
        [field]: value,
      },
    }));
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const moveDraw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;

    ctx.lineTo(x, y);
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2;
    ctx.stroke();
  };

  const endDraw = (e) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  const openSignatureModal = (type) => {
    setError('');
    setSuccessMessage('');
    setSignModal({ open: true, type });
    setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      //ctx.fillStyle = '#ffffff';
      //ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }, 50);
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dataUrl = canvas.toDataURL('image/png');

    if (signModal.type === 'tecnico') {
      setTecnicoSignature(dataUrl);
    } else if (signModal.type === 'cliente') {
      setClienteSignature(dataUrl);
    }

    setSignModal({ open: false, type: null });
  };

  const clearSignaturePreview = (type) => {
    if (type === 'tecnico') {
      setTecnicoSignature(null);
    } else if (type === 'cliente') {
      setClienteSignature(null);
    }
  };

  // =========================
  // GENERA PDF + PATCH WorkInProgress
  // =========================
  const handleGeneratePdf = async () => {
    setError('');
    setSuccessMessage('');

    if (!formData.data || !formData.cliente) {
      setError('Compila almeno data e cliente prima di generare il PDF.');
      return;
    }

    setLoading(true);

    try {
      const body = {
        template: selectedTemplate,
        formData,
        checks,
        tecnicoSignature,
        clienteSignature,
      };

      const blob = await generateManutenzionePdfBlob({ ...body, inviaReport: true });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const code =
        summary?.codiceInterno ||
        formData?.codiceInterno ||
        order?.internalCode ||
        'MXX-00000';

      a.download = `verbale_manutenzione_${code}.pdf`;


      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setSuccessMessage('PDF generato correttamente.');

      // 🔥 Salvo nel WorkInProgress: summary aggiornato + formData + checks + firme
      if (workInProgressId) {
        const newSummary = {
          ...summary,
          codiceInterno:
            summary.codiceInterno ||
            order.internalCode ||
            order.codiceInterno ||
            '',
          codiceEsterno:
            summary.codiceEsterno ||
            order.codiceEsterno ||
            order.externalCode ||
            '',
          data: formData.data,
          cliente: formData.cliente,
          impianto: formData.impianto,
          luogo: formData.luogo,
          modello:
            summary.modello ||
            order.modello ||
            order.equipmentModel ||
            '',
          matricola:
            summary.matricola ||
            order.seriale ||
            order.matricola ||
            order.equipmentSerial ||
            '',
          inventario:
            summary.inventario ||
            order.inventario ||
            order.equipmentInventory ||
            '',
          note: formData.note,
        };

        try {
          await authFetch(
            `${API_BASE}/work/in-progress/${workInProgressId}`,
            {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                summary: newSummary,
                formData,
                checks,
                tecnicoSignature,
                clienteSignature,
                status: 'chiusa', 
              }),
            }
          );
        } catch (err2) {
          console.error(
            'Errore aggiornamento summary/formData/checks/firme (MANUTENZIONI COMPILATE):',
            err2
          );
        }
      }
    } catch (err) {
      console.error('Errore generazione PDF manutenzione compilata:', err);
      setError(err.message || 'Errore inatteso durante la generazione');
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // RENDER
  // =========================
  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      <div className="max-w-6xl mx-auto px-4 pt-8">
        {/* HEADER */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">
              Verbale di manutenzione (report compilato)
            </h1>
            <p className="text-sm text-gray-600">
              Qui puoi verificare e correggere i dati della manutenzione già compilata:
              verifiche, note, firme, ecc.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/lavorazioni/in-corso')}
            className="hidden md:inline-flex px-3 py-1.5 rounded-lg text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            ← Indietro
          </button>
        </div>

        {/* Template */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          {templates.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelectedTemplate(t.id)}
              className={`w-full text-left p-4 rounded-xl border transition shadow-sm ${
                selectedTemplate === t.id
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-blue-400'
              }`}
            >
              <h2 className="font-semibold text-gray-800">{t.label}</h2>
              <p className="text-xs text-gray-500 mt-1">
                Modello PDF: {t.file.replace('/Manutenzione/', '')}
              </p>
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[2.1fr,3fr] gap-8">
          {/* SX: form + verifiche + firme */}
          <div className="space-y-6">
            {/* Dati intervento */}
            <Card className="shadow-sm p-4 overflow-hidden">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Dati intervento
              </h3>
              <div className="grid sm:grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Data
                  </label>
                  <input
                    type="date"
                    name="data"
                    value={formData.data}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Cliente
                  </label>
                  <input
                    type="text"
                    name="cliente"
                    value={formData.cliente}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Impianto / Presidio
                  </label>
                  <input
                    type="text"
                    name="impianto"
                    value={formData.impianto}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Luogo intervento
                  </label>
                  <input
                    type="text"
                    name="luogo"
                    value={formData.luogo}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Note (facoltative)
                </label>
                <textarea
                  name="note"
                  value={formData.note}
                  onChange={handleChange}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </Card>

            {/* Verifiche */}
            <Card className="shadow-sm p-4 space-y-3">
              <h3 className="text-lg font-semibold text-gray-800 mb-1">
                Verifiche eseguite
              </h3>
              <p className="text-xs text-gray-500 mb-2">
                Le verifiche sono state ripescate da chi ha svolto la manutenzione.
                Puoi correggerle se necessario.
              </p>

              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <div key={n} className="space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <Checkbox
                      checked={checks[n].ok}
                      onChange={() => toggleCheck(n, 'ok')}
                    />
                    <span>{CHECK_LABELS[n]}</span>
                  </label>
              
                  {n === 8 && (
                    <input
                      type="text"
                      value={checks[8].note || ''}
                      onChange={(e) => handleCheckValueChange(8, 'note', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                      placeholder="Valore / nota verifica 8 (facoltativo)"
                      disabled={!checks[8].ok}
                    />
                  )}
                </div>
              ))}

              {/* 9 con value + NA */}
              <div className="border-t border-gray-100 pt-2 mt-2 space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={!!checks[9]?.ok}
                    onChange={() => toggleCheck(9, 'ok')}
                  />
                  <span>{CHECK_LABELS[9]}</span>

                </label>
                <div className="grid grid-cols-[1fr,auto] gap-2 items-center">
                  <input
                    type="text"
                    value={checks[9]?.value || ''}
                    onChange={(e) =>
                      handleCheckValueChange(9, 'value', e.target.value)
                    }
                    disabled={!!checks[9]?.na}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                    placeholder="Valore rilevato"
                  />
                  <label className="flex items-center gap-1 text-xs text-gray-600">
                    <Checkbox
                      checked={!!checks[9]?.na}
                      onChange={() => toggleCheck(9, 'na')}
                    />
                    N/A
                  </label>
                </div>
              </div>

              {/* 10 con NA */}
              <div className="border-t border-gray-100 pt-2 mt-2 space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={!!checks[10]?.ok}
                    onChange={() => toggleCheck(10, 'ok')}
                  />
                  <span>{CHECK_LABELS[10]}</span>

                </label>
                <label className="flex items-center gap-1 text-xs text-gray-600">
                  <Checkbox
                    checked={!!checks[10]?.na}
                    onChange={() => toggleCheck(10, 'na')}
                  />
                  N/A
                </label>
              </div>

              {/* 11 */}
              <div className="border-t border-gray-100 pt-2 mt-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={!!checks[11]?.ok}
                    onChange={() => toggleCheck(11, 'ok')}
                  />
                  <span>{CHECK_LABELS[11]}</span>
                </label>
              </div>

              {/* 12: T set / T mis + NA */}
              <div className="border-t border-gray-100 pt-2 mt-2 space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={!!checks[12]?.ok}
                    onChange={() => toggleCheck(12, 'ok')}
                  />
                  <span>{CHECK_LABELS[12]}</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={checks[12]?.tSet || ''}
                    onChange={(e) =>
                      handleCheckValueChange(12, 'tSet', e.target.value)
                    }
                    disabled={!!checks[12]?.na}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                    placeholder="T set"
                  />
                  <input
                    type="text"
                    value={checks[12]?.tMis || ''}
                    onChange={(e) =>
                      handleCheckValueChange(12, 'tMis', e.target.value)
                    }
                    disabled={!!checks[12]?.na}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                    placeholder="T mis"
                  />
                </div>
                <label className="flex items-center gap-1 text-xs text-gray-600">
                  <Checkbox
                    checked={!!checks[12]?.na}
                    onChange={() => toggleCheck(12, 'na')}
                  />
                  N/A
                </label>
              </div>

              {/* 13: UR set / UR mis + NA */}
              <div className="border-t border-gray-100 pt-2 mt-2 space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={!!checks[13]?.ok}
                    onChange={() => toggleCheck(13, 'ok')}
                  />
                  <span>{CHECK_LABELS[13]}</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={checks[13]?.urSet || ''}
                    onChange={(e) =>
                      handleCheckValueChange(13, 'urSet', e.target.value)
                    }
                    disabled={!!checks[13]?.na}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                    placeholder="UR set"
                  />
                  <input
                    type="text"
                    value={checks[13]?.urMis || ''}
                    onChange={(e) =>
                      handleCheckValueChange(13, 'urMis', e.target.value)
                    }
                    disabled={!!checks[13]?.na}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                    placeholder="UR mis"
                  />
                </div>
                <label className="flex items-center gap-1 text-xs text-gray-600">
                  <Checkbox
                    checked={!!checks[13]?.na}
                    onChange={() => toggleCheck(13, 'na')}
                  />
                  N/A
                </label>
              </div>

              {/* 14 con NA */}
              <div className="border-t border-gray-100 pt-2 mt-2 space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={!!checks[14]?.ok}
                    onChange={() => toggleCheck(14, 'ok')}
                  />
                  <span>{CHECK_LABELS[14]}</span>
                </label>
                <label className="flex items-center gap-1 text-xs text-gray-600">
                  <Checkbox
                    checked={!!checks[14]?.na}
                    onChange={() => toggleCheck(14, 'na')}
                  />
                  N/A
                </label>
              </div>

              {/* 15 note */}
              <div className="border-t border-gray-100 pt-2 mt-2 space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={!!checks[15]?.ok}
                    onChange={() => toggleCheck(15, 'ok')}
                  />
                  <span>{CHECK_LABELS[15]}</span>
                </label>
                <textarea
                  value={checks[15]?.note || ''}
                  onChange={(e) =>
                    handleCheckValueChange(15, 'note', e.target.value)
                  }
                  rows={2}
                  className="border border-gray-300 rounded-lg px-2 py-1 text-sm w-full"
                  placeholder="Note aggiuntive"
                />
              </div>
            </Card>

            {/* FIRME + GENERA PDF */}
            <Card className="shadow-sm p-4 space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Firme</h3>
              <p className="text-xs text-gray-500 mb-1">
                Le firme sono state salvate come immagini. Se necessario puoi
                aggiungerle o modificarle.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {/* Firma tecnico */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">
                      Firma tecnico
                    </p>
                    <button
                      type="button"
                      onClick={() => openSignatureModal('tecnico')}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      {tecnicoSignature ? 'Modifica firma' : 'Aggiungi firma'}
                    </button>
                  </div>
                  {tecnicoSignature ? (
                    <div className="border border-gray-300 rounded-lg p-2 bg-gray-50">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] text-gray-600">
                          Firma tecnico salvata.
                        </span>
                        <button
                          type="button"
                          onClick={() => clearSignaturePreview('tecnico')}
                          className="text-[11px] text-gray-500 hover:text-gray-700"
                        >
                          Cancella firma
                        </button>
                      </div>
                      {/* preview immagine */}
                      <div className="border border-gray-200 rounded bg-white p-1">
                        <img
                          src={tecnicoSignature}
                          alt="Firma tecnico"
                          className="max-h-24 mx-auto"
                        />
                      </div>
                    </div>
                  ) : (
                    <p className="text-[11px] text-gray-500">
                      Nessuna firma tecnica presente.
                    </p>
                  )}
                </div>

                {/* Firma cliente */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">
                      Firma cliente
                    </p>
                    <button
                      type="button"
                      onClick={() => openSignatureModal('cliente')}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      {clienteSignature ? 'Modifica firma' : 'Aggiungi firma'}
                    </button>
                  </div>
                  {clienteSignature ? (
                    <div className="border border-gray-300 rounded-lg p-2 bg-gray-50">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] text-gray-600">
                          Firma cliente salvata.
                        </span>
                        <button
                          type="button"
                          onClick={() => clearSignaturePreview('cliente')}
                          className="text-[11px] text-gray-500 hover:text-gray-700"
                        >
                          Cancella firma
                        </button>
                      </div>
                      <div className="border border-gray-200 rounded bg-white p-1">
                        <img
                          src={clienteSignature}
                          alt="Firma cliente"
                          className="max-h-24 mx-auto"
                        />
                      </div>
                    </div>
                  ) : (
                    <p className="text-[11px] text-gray-500">
                      Nessuna firma cliente presente.
                    </p>
                  )}
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}
              {successMessage && (
                <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                  {successMessage}
                </p>
              )}

              <button
                type="button"
                onClick={handleGeneratePdf}
                disabled={loading}
                className={`w-full py-2.5 rounded-lg text-sm font-semibold text-white shadow-sm transition ${
                  loading
                    ? 'bg-blue-300 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? 'Generazione in corso...' : 'Genera PDF aggiornato'}
              </button>
            </Card>
          </div>

          {/* DX: anteprima PDF */}
          <Card className="shadow-sm p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Anteprima modello
            </h3>
            {pdfUrl ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden h-[600px]">
                <iframe
                  title="Anteprima PDF Manutenzione"
                  src={pdfUrl}
                  className="w-full h-full"
                />
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Seleziona un modello per visualizzare l&apos;anteprima.
              </p>
            )}
          </Card>
        </div>
      </div>

      {/* Modal firma full-screen */}
      {signModal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onTouchMove={(e) => e.preventDefault()}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-gray-800">
                Firma {signModal.type === 'tecnico' ? 'tecnico' : 'cliente'}
              </h2>
              <button
                onClick={() => setSignModal({ open: false, type: null })}
                className="text-gray-500 hover:text-gray-800 text-xl leading-none"
              >
                &times;
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              Firma con il dito o con il mouse. Al termine premi &quot;Salva
              firma&quot;.
            </p>
            <div className="border border-gray-300 rounded-lg bg-gray-50">
              <canvas
                ref={canvasRef}
                width={400}
                height={200}
                className="w-full h-48"
                onMouseDown={startDraw}
                onMouseMove={moveDraw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={moveDraw}
                onTouchEnd={endDraw}
              />
            </div>
            <div className="mt-3 flex justify-between items-center">
              <button
                type="button"
                onClick={() => {
                  const canvas = canvasRef.current;
                  if (!canvas) return;
                  const ctx = canvas.getContext('2d');
                  //ctx.fillStyle = '#ffffff';
                  //ctx.fillRect(0, 0, canvas.width, canvas.height);
                  ctx.clearRect(0, 0, canvas.width, canvas.height);
                }}
                className="text-xs text-gray-600 hover:text-gray-800"
              >
                Cancella
              </button>
              <button
                type="button"
                onClick={saveSignature}
                className="px-4 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
              >
                Salva firma
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManutenzioniCompilate;
